import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFacilitatorDetail, FacilitatorDetail as Detail } from '../../services/api';
import MonitorLayout from './MonitorLayout';

/** Middle of the facilitator drill-down: facilitator → SCHOOLS → full detail.
 *  A facilitator belongs to one school in the data model, but we still surface
 *  the school as a card so the path is consistent and future-proof for
 *  multi-school facilitators. Clicking a school opens the existing, unchanged
 *  detail page (the full activity). */

type ClassAttn = Detail['class_attendance'][number];

const relLabel = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Active today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
};

/* There is deliberately NO attendance maths in this file any more. It used to
 * average the per-class percentages here, weighted by enrolment — a second
 * definition of attendance, and an average of averages, which could disagree
 * with the school page you land on after clicking the card. The figure now comes
 * from the API's `school_attendance`, computed once on the server exactly as the
 * school-scoped page computes it. */

const FacilitatorSchools: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('monitorToken');
    if (!token) {
      navigate('/monitor/login', { replace: true });
      return;
    }
    if (!id) return;
    getFacilitatorDetail(token, Number(id))
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('monitorToken');
          navigate('/monitor/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Group the facilitator's classes by school. Fall back to their registered
  // school (with no classes) so a facilitator with no roster still shows a card.
  //
  // The attendance and student count come from the API's per-school figures,
  // which are worked out exactly as the school-scoped page works them out —
  // clicking a card must never change the number it showed you. The old
  // class-weighted estimate is the fallback for a facilitator with no
  // per-school data at all.
  const schools = useMemo(() => {
    const m = new Map<string, ClassAttn[]>();
    (d?.class_attendance ?? []).forEach((c) => {
      if (!m.has(c.school)) m.set(c.school, []);
      m.get(c.school)!.push(c);
    });
    (d?.school_attendance ?? []).forEach((s) => {
      if (!m.has(s.school)) m.set(s.school, []);
    });
    if (m.size === 0 && d?.school) m.set(d.school, []);
    const bySchool = new Map((d?.school_attendance ?? []).map((s) => [s.school, s]));
    return Array.from(m.entries()).map(([school, classes]) => {
      const api = bySchool.get(school);
      return {
        school,
        classes,
        students: api ? api.enrolled : classes.reduce((s, c) => s + c.enrolled, 0),
        attendance: api ? api.attendance_pct : null,
        sessions: api ? api.sessions : null,
      };
    });
  }, [d]);

  const openDetail = () => navigate(`/monitor/facilitators/${id}/detail`);
  // A school card opens the SAME page scoped to that school, so attendance and
  // the other per-school figures read for one school at a time (Athira,
  // 2026-08-26: attendance moves from the facilitator page into the school).
  const openSchool = (school: string) =>
    navigate(`/monitor/facilitators/${id}/detail?school=${encodeURIComponent(school)}`);

  return (
    <MonitorLayout>
      <button
        type="button"
        onClick={() => navigate('/monitor/facilitators')}
        className="mb-4 text-sm font-semibold text-slate-500 hover:text-slate-800"
      >
        ← Facilitators
      </button>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : error || !d ? (
        <p className="py-10 text-center text-sm text-rose-600">{error || 'Not found.'}</p>
      ) : (
        <>
          {/* Facilitator header */}
          <div className="rounded-3xl bg-white px-6 py-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-lg font-bold text-violet-700">
                {(d.name || d.username).slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">{d.name || d.username}</h1>
                <p className="text-sm text-slate-500">@{d.username}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Last active</p>
                <p className="text-sm font-semibold text-slate-700">{relLabel(d.last_active)}</p>
              </div>
              <button
                type="button"
                onClick={openDetail}
                className="rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100"
              >
                Full activity →
              </button>
            </div>
          </div>

          {/* Schools — click one to open the facilitator's full activity */}
          <div className="mt-6">
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">
              Schools · {schools.length}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {schools.map((s) => (
                <button
                  key={s.school}
                  type="button"
                  onClick={() => openSchool(s.school)}
                  className="group rounded-2xl border border-[#E9E6F4] bg-white p-5 text-left shadow-sm transition hover:border-violet-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-violet-100 text-base font-bold text-violet-700">
                      {s.school.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-violet-500">→</span>
                  </div>
                  <p className="mt-3 truncate text-base font-bold text-slate-900">{s.school}</p>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-slate-500">
                    <span title="How many of this facilitator's classes are in this school.">
                      <b className="text-slate-800">{s.classes.length}</b> class{s.classes.length === 1 ? '' : 'es'}
                    </span>
                    <span title="Students across those classes, each child counted once even if two of the facilitator's classes both include them (e.g. a 'whole Grade 7' class and a 'Grade 7-B' class).">
                      <b className="text-slate-800">{s.students}</b> students
                    </span>
                    <span
                      className="cursor-help"
                      title={
                        s.sessions === 0
                          ? 'This facilitator has not held a session in this school yet, so there is nothing to measure attendance against.'
                          : 'On the days this facilitator held a session IN THIS SCHOOL, how many of their students here used the coach that day. The facilitator page shows the average across their schools.'
                      }
                    >
                      Attendance <b className="text-slate-800">{s.attendance == null ? '—' : `${s.attendance}%`}</b>
                      {s.sessions === 0 ? (
                        <span className="ml-1 text-slate-400">(no sessions yet)</span>
                      ) : null}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </MonitorLayout>
  );
};

export default FacilitatorSchools;
